import { createTheme, Theme } from "@mui/material/styles";

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    section: true;
    dialog: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#C79A61", // beige doré
      light: "#ECDCC7", // beige très clair
      dark: "#A8753F",
    },
    secondary: {
      main: "#E6D3BA", // beige clair
    },
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
    },
    divider: "#C79A61", // pour encadrer les tableaux/alertes
    text: {
      primary: "#3E2F1C", // marron foncé lisible
      secondary: "#5B3D23", // marron moyen chaud
    },
  },
  typography: {
    fontFamily: '"Inter", "Poppins", "Roboto", sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "@keyframes fadeInSlide": {
          from: {
            opacity: 0,
            transform: "translateY(-8px)",
          },
          to: {
            opacity: 1,
            transform: "translateY(0)",
          },
        },
        "@global": {
          // Animation pour contrer le fond autofill imposé par Chrome
          "@keyframes autofill-fade": {
            to: {
              backgroundColor: "#f7f2e8",
            },
          },
          "input:-webkit-autofill": {
            boxShadow: "0 0 0 1000px #fff inset !important",
            WebkitTextFillColor: "#000",
            borderRadius: "12px",
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          fontWeight: 600,
          color: "#3E2F1C",
        },
        body1: {
          fontSize: "1rem",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#C79A61",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          transition: "all 0.3s ease",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -4,
            left: 0,
            width: 0,
            height: 2,
            backgroundColor: "#fff",
            transition: "width 0.3s ease-in-out",
          },
          "&:hover::after": {
            width: "100%",
          },
          "&:hover": {
            backgroundColor: "transparent",
            color: "#E6D3BA",
          },
          "&.match-action-button": {
            "&::after": {
              display: "none", // annule la barre blanche
            },
            "&:hover::after": {
              display: "none",
            },
            "&:hover": {
              backgroundColor: "#C79A61 !important", // couleur fixe au hover
              color: "#fff !important",
              boxShadow: "none !important", // supprime ombres
              filter: "none !important", // supprime tout filtre
            },
            "&:focus-visible": {
              boxShadow: "none !important",
              outline: "none !important",
            },
            "&:active": {
              boxShadow: "none !important",
              backgroundColor: "#C79A61 !important",
            },
          },
        },
      },
      variants: [
        {
          props: { variant: "contained", color: "primary" },
          style: ({ theme }: { theme: Theme }) => ({
            color: "#fff",
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[4],
            },
          }),
        },
        {
          props: { variant: "contained", color: "secondary" },
          style: ({ theme }: { theme: Theme }) => ({
            color: "#3E2F1C",
            backgroundColor: theme.palette.secondary.main,
            "&:hover": {
              backgroundColor: "#f0e4d4",
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[4],
            },
          }),
        },
      ],
    },

    MuiPaper: {
      variants: [
        {
          props: { variant: "section" },
          style: {
            borderRadius: 16,
            padding: "1.5rem",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            backgroundColor: "#fff",
          },
        },
        {
          props: { variant: "dialog" },
          style: {
            borderRadius: 12,
            padding: "2rem",
            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.15)",
            backgroundColor: "#fefefe",
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          transition: "transform 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          left: 0,
          right: 0,
          position: "sticky",

          // Style par défaut (pour sm et +)
          borderRadius: theme.shape.borderRadius,
          width: "calc(100% - 12px)",
          margin: "6px auto",

          // Style spécifique aux écrans XS (mobile)
          [theme.breakpoints.down("sm")]: {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: theme.shape.borderRadius,
            borderBottomRightRadius: theme.shape.borderRadius,
            width: "100%",
            margin: 0,
          },
        }),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        regular: {
          minHeight: "48px",
          "@media (min-width:600px)": {
            minHeight: "80px",
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderTopLeftRadius: 20,
          borderBottomLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          boxShadow: "4px 0 12px rgba(0,0,0,0.2)",
          transition: "border-radius 0.3s ease",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: "#fff",
          justifyContent: "center",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            color: "#E6D3BA",
          },
          "& .MuiListItemText-primary": {
            fontWeight: 500,
            fontSize: "1rem",
            letterSpacing: 0.5,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: ({ theme }: { theme: Theme }) => ({
          width: "100%",
          overflowX: "auto",
          border: `2px solid ${theme.palette.primary.main}`,
          boxShadow: theme.shadows[2],
          backgroundColor: theme.palette.secondary.light,
          color: theme.palette.text.primary,
          "& .MuiAlert-icon": {
            display: "none",
          },
          fontWeight: 500,
          fontSize: "1rem",
          letterSpacing: 0.5,
          position: "relative",
          paddingLeft: theme.spacing(5),
          animation: "fadeInSlide 0.6s ease",
          "&::before": {
            content: '"🎉"',
            position: "absolute",
            left: theme.spacing(2),
            top: theme.spacing(1.5),
            fontSize: "1.25rem",
          },
        }),
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: theme.shape.borderRadius,
          overflowX: "auto",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.primary.main + "22",
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          "&:nth-of-type(odd)": {
            backgroundColor: theme.palette.secondary.light + "33",
          },
          "&:hover": {
            backgroundColor: theme.palette.primary.light + "33",
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }: { theme: Theme }) => ({
          fontWeight: 600,
          fontSize: "1rem",
          color: theme.palette.primary.main,
        }),
        root: ({ theme }: { theme: Theme }) => ({
          fontSize: "0.95rem",
          color: theme.palette.text.primary,
          padding: theme.spacing(1.5, 2),
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        fullWidth: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          transition: theme.transitions.create(["background-color"], {
            duration: theme.transitions.duration.short,
          }),
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "&.Mui-focused": {
            backgroundColor: theme.palette.primary.light,
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.action.disabledBackground,
          },
          "&.Mui-error.Mui-focused": {
            backgroundColor: theme.palette.error.light,
          },
          marginBottom: "16px",
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          backgroundColor: theme.palette.background.paper,
          transition: theme.transitions.create(
            ["background-color", "border-color"],
            {
              duration: theme.transitions.duration.short,
            }
          ),
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
            borderRadius: 12,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.dark,
            borderWidth: 2,
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.action.disabledBackground,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.error.main,
          },
        }),
        notchedOutline: {
          borderWidth: 2,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          "&.Mui-focused": {
            color: theme.palette.primary.main,
          },
          "&.Mui-error": {
            color: theme.palette.error.main,
          },
        }),
      },
    },
  },
});

export default theme;
